import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { api } from "../api";
import { when } from "../accounts";
import {
  actionLabel,
  AUDIT_GROUPS,
  auditActorLabel,
  detailText,
  filtersFromParams,
  filtersProblem,
  filtersQuery,
  groupActions,
  NO_FILTERS,
  TARGET_TYPES,
  targetLabel,
  targetPath,
  type AuditEntry,
  type AuditFilters,
} from "../audit";
import { errorText, unwrap } from "../problem";

// The filters live in the URL, so other pages link here filtered (a staff
// member's activity, an account's history). Reading the log is not audited.
export function AuditPage() {
  const [params, setParams] = useSearchParams();
  const filters = filtersFromParams(params);
  const query = filtersQuery(filters);
  const problem = filtersProblem(filters);
  const staff = useQuery({ queryKey: ["staff"], queryFn: async () => unwrap(await api.GET("/staff")) });
  const log = useInfiniteQuery({
    queryKey: ["audit", query],
    queryFn: async ({ pageParam }) =>
      unwrap(await api.GET("/audit", { params: { query: { ...query, ...(pageParam ? { cursor: pageParam } : {}) } } })),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    enabled: problem === null,
  });
  const entries = log.data?.pages.flatMap((page) => page.entries) ?? [];
  const apply = (next: AuditFilters) => setParams(filtersQuery(next));
  // Staff to filter by: today's, plus whoever the URL names.
  const people = (staff.data?.staff ?? []).map((member) => ({ id: member.user_id, label: member.email }));
  if (filters.staff && filters.staff !== "none" && !people.some((person) => person.id === filters.staff))
    people.push({ id: filters.staff, label: filters.staff });

  return (
    <>
      <h1>Audit log</h1>
      <p className="muted">
        Everything staff do in the console and the CLI, sign-ins and every look at an account or circle, newest first.
        Rows are never changed or deleted.
      </p>
      <Filters key={params.toString()} filters={filters} people={people} onApply={apply} />
      {problem && <p className="error">{problem}</p>}
      {log.isPending && problem === null && <p>Loading…</p>}
      {log.isError && <p className="error">{errorText(log.error)}</p>}
      {log.data && (
        <>
          {entries.length === 0 ? (
            <p className="muted">Nothing matches.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <Row key={entry.id} entry={entry} filters={filters} onFilter={apply} />
                ))}
              </tbody>
            </table>
          )}
          {log.hasNextPage && (
            <button
              type="button"
              className="button-quiet"
              onClick={() => void log.fetchNextPage()}
              disabled={log.isFetchingNextPage}
            >
              {log.isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          )}
        </>
      )}
    </>
  );
}

function Row({
  entry,
  filters,
  onFilter,
}: {
  entry: AuditEntry;
  filters: AuditFilters;
  onFilter: (filters: AuditFilters) => void;
}) {
  const path = targetPath(entry.target);
  const actor = entry.actor.user_id ?? "none";
  return (
    <tr>
      <td>{when(entry.at)}</td>
      <td>
        <button
          type="button"
          className="link"
          title="Only this actor"
          onClick={() => onFilter({ ...filters, staff: actor })}
        >
          {auditActorLabel(entry.actor)}
        </button>
      </td>
      <td>
        <button
          type="button"
          className="link"
          title="Only this action"
          onClick={() => onFilter({ ...filters, action: entry.action })}
        >
          {actionLabel(entry.action)}
        </button>{" "}
        <code className="muted">{entry.action}</code>
      </td>
      <td>
        {entry.target ? (
          <>
            {path ? <Link to={path}>{targetLabel(entry.target)}</Link> : targetLabel(entry.target)}{" "}
            <button
              type="button"
              className="link muted"
              title="Only this target"
              onClick={() => onFilter({ ...filters, target_type: entry.target!.type, target_id: entry.target!.id })}
            >
              (filter)
            </button>
          </>
        ) : (
          "—"
        )}
      </td>
      <td className="prewrap">{detailText(entry.detail) || "—"}</td>
    </tr>
  );
}

function Filters({
  filters,
  people,
  onApply,
}: {
  filters: AuditFilters;
  people: { id: string; label: string }[];
  onApply: (filters: AuditFilters) => void;
}) {
  const [draft, setDraft] = useState(filters);
  const set = (key: keyof AuditFilters) => (value: string) => setDraft((current) => ({ ...current, [key]: value }));
  const known =
    draft.action === "" ||
    AUDIT_GROUPS.some(({ group }) => group === draft.action || groupActions(group).includes(draft.action));
  function submit(event: FormEvent) {
    event.preventDefault();
    onApply(draft.target_type ? draft : { ...draft, target_id: "" });
  }
  return (
    <form className="filters" onSubmit={submit}>
      <label>
        Staff
        <select value={draft.staff} onChange={(event) => set("staff")(event.target.value)}>
          <option value="">Anyone</option>
          {people.map((person) => (
            <option key={person.id} value={person.id}>
              {person.label}
            </option>
          ))}
          <option value="none">CLI or server</option>
        </select>
      </label>
      <label>
        Action
        <select value={draft.action} onChange={(event) => set("action")(event.target.value)}>
          <option value="">Any</option>
          {!known && <option value={draft.action}>{draft.action}</option>}
          {AUDIT_GROUPS.map(({ group, label }) => (
            <optgroup key={group} label={label}>
              <option value={group}>All {label.toLowerCase()}</option>
              {groupActions(group).map((action) => (
                <option key={action} value={action}>
                  {actionLabel(action)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>
      <label>
        Target
        <select value={draft.target_type} onChange={(event) => set("target_type")(event.target.value)}>
          <option value="">Any</option>
          {!TARGET_TYPES.some(({ type }) => type === draft.target_type) && draft.target_type && (
            <option value={draft.target_type}>{draft.target_type}</option>
          )}
          {TARGET_TYPES.map(({ type, label }) => (
            <option key={type} value={type}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <label className="grow">
        Target id
        <input
          value={draft.target_id}
          disabled={!draft.target_type}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => set("target_id")(event.target.value)}
        />
      </label>
      <button type="submit">Show</button>
      <button type="button" className="button-quiet" onClick={() => onApply(NO_FILTERS)}>
        Clear
      </button>
    </form>
  );
}
